// frontend/src/services/pushSubscriptionService.js
/**
 * Push Subscription Service
 *
 * Manages the full Web Push subscription lifecycle on the frontend:
 *   - Browser support detection
 *   - Notification permission request
 *   - VAPID subscribe / unsubscribe (synced with backend)
 *   - VAPID public key fetching (cached per session)
 *   - The per-device "user deliberately turned push off" marker, which is what
 *     lets a logout-revoked subscription be restored automatically without
 *     overriding a user who chose to disable push (see PUSH_OPT_OUT_KEY)
 *
 * All public methods return a boolean or value — none throw to the caller.
 * Errors are logged to the console only.
 *
 * API endpoints used (relative to /api/v1):
 *   GET  /users/push/vapid-public-key
 *   POST /users/push/subscription
 *   DELETE /users/push/subscription
 *
 * ---------------------------------------------------------------------------
 * TIME BOUNDS (backlog #160)
 * ---------------------------------------------------------------------------
 * The enable flow used to be four unbounded awaits in a row, so a stalled push
 * service left the CTA disabled and `aria-busy` forever with nothing on screen.
 * The fix is NOT a race that resolves false - that would show a failure while
 * the subscription later comes up, which is worse than the hang. Instead:
 *
 *   - `startSubscribe()` returns TWO things: a bounded `outcome` for the UI to
 *     render right now, and a never-rejecting `settled` promise carrying the
 *     REAL outcome whenever the attempt finishes. PENDING means "still running",
 *     never "failed".
 *   - Per-step bounds are mostly DIAGNOSTIC: they log WHICH step stalled
 *     (`[Push] stalled at sw_ready|vapid_key|push_subscribe|backend_post`) - the
 *     one thing the old code could not tell us - without abandoning a promise
 *     that may still succeed. `serviceWorker.ready` is the single exception: a
 *     registration that never activates cannot produce a subscription later, so
 *     nothing is lost by failing fast and there is nothing to reconcile.
 *   - `reconcileSubscription()` is the post-hoc read: no prompt, no creation,
 *     idempotent re-POST only, and it reports `unknown` rather than a failure
 *     when the read itself is inconclusive.
 */

import api from './api';
import analytics from './analytics';

// ---------------------------------------------------------------------------
// Instrumentation
// ---------------------------------------------------------------------------

/**
 * Which UI asked for this subscribe/unsubscribe.
 *
 * The DEFAULT IS 'unknown', deliberately, and it is not laziness: the install
 * banner calls subscribe() with no arguments. Defaulting to 'settings' would
 * file that under a surface the user never touched and make the Settings
 * numbers the one thing this event exists to measure - wrong. Every call site
 * that knows its surface passes it ('settings', 'nudge', 'install', 'logout');
 * the ones that do not say so honestly.
 *
 * 'reconcile' is the one surface no button owns: a subscription that came good
 * in the background and was noticed by reconcileSubscription() rather than by
 * the attempt itself. It is a real enablement and has to be counted, so it gets
 * its own name rather than borrowing the CTA's.
 *
 * JOIN ATTRIBUTION: a second caller that joins an attempt already in flight
 * does NOT get its own event - the single `push_subscribed` carries the
 * STARTING surface. So an install banner firing subscribe('install') beside a
 * running nudge attempt is counted as 'nudge'. That is the honest reading: one
 * subscription was created, by whichever surface actually started it.
 */
const DEFAULT_SURFACE = 'unknown';

/** The surface used when a background reconciliation is what noticed. */
const RECONCILE_SURFACE = 'reconcile';

/**
 * The one surface whose revocation is NOT an opt-out.
 *
 * Logout revokes this device's subscription as HYGIENE - `push_subscriptions`
 * is unique on (user_id, endpoint), so one browser must not hold live
 * subscriptions for two accounts. The user did not turn push off; the very next
 * login restores it silently (silentResubscribe), and no opt-out marker is
 * written. Emitting push_unsubscribed here would count every healthy user's
 * sign-out as an opt-out - on the admin tile, the busiest users would look like
 * the ones abandoning push. So the event is suppressed for this surface only;
 * the revocation itself is unchanged.
 */
const SILENT_UNSUBSCRIBE_SURFACE = 'logout';

/**
 * Fire-and-forget product event. Wrapped because this service's contract is
 * that no public method throws to its caller, and instrumentation must not be
 * the thing that breaks it.
 */
const track = (eventName, params) => {
  try {
    analytics.trackEvent(eventName, params);
  } catch (err) {
    /* analytics must never break the push lifecycle */
  }
};

// ---------------------------------------------------------------------------
// Subscribe outcomes + time bounds
// ---------------------------------------------------------------------------

/**
 * What an enable attempt did. SIX values, and the two that look redundant are
 * the ones that matter:
 *
 *   DENIED     the user pressed Block. PERMANENT - no browser lets script
 *              re-prompt, so a UI must stop offering a retry.
 *   DISMISSED  the user closed the prompt without choosing (permission stays
 *              'default'). Retrying is legitimate and will prompt again.
 *   PENDING    the attempt has NOT finished and has NOT failed. The only honest
 *              thing to render is "still working"; the `settled` handle carries
 *              the real answer if it ever arrives.
 */
export const SUBSCRIBE_OUTCOME = Object.freeze({
  SUCCESS: 'success',
  DENIED: 'denied',
  DISMISSED: 'dismissed',
  UNSUPPORTED: 'unsupported',
  FAILED: 'failed',
  PENDING: 'pending',
});

/**
 * What a reconciliation READ concluded. `unknown` is not a failure - it is the
 * absence of a finding, and a UI must never render it as one.
 */
const RECONCILE_RESULT = Object.freeze({
  SUCCESS: 'success',
  NOT_SUBSCRIBED: 'not_subscribed',
  UNKNOWN: 'unknown',
});

/**
 * How long the UI waits before it stops claiming to be mid-action.
 *
 * The clock starts AFTER the permission prompt resolves, never before: the
 * prompt is a modal the user may leave open for a minute, and timing it would
 * report a failure for a person who is simply reading it.
 */
const SUBSCRIBE_WALL_CLOCK_MS = 15000;

/**
 * The ONE abortive bound. `navigator.serviceWorker.ready` never rejects, so a
 * registration that failed (index.js registers with no `.catch`) leaves it
 * pending for the life of the document - the likeliest real-world hang. Nothing
 * has been created at that point, so failing fast costs nothing and there is no
 * orphan to reconcile.
 */
const SW_READY_TIMEOUT_MS = 3000;

// Diagnostic-only bounds. Crossing one logs which step is slow; the await
// continues, because abandoning `pushManager.subscribe()` is exactly how a
// browser subscription ends up existing with no backend row behind it.
const VAPID_KEY_STALL_MS = 8000;
const PUSH_SUBSCRIBE_STALL_MS = 10000; // iOS/APNs first grant is genuinely slow
const BACKEND_POST_STALL_MS = 8000;

// Bounds for the read-only reconciliation path.
const GET_SUBSCRIPTION_TIMEOUT_MS = 3000;
const RECONCILE_TIMEOUT_MS = 5000;

/**
 * How long a background path waits on an in-flight enable attempt.
 *
 * Deliberately SHORT and independent of the attempt's own wall clock: the
 * attempt may be parked on a permission prompt that never resolves, and a
 * background path must never inherit that.
 */
const SUBSCRIBE_BARRIER_WAIT_MS = 3000;

/** Distinguishes "the bound elapsed" from any value a step could return. */
const TIMED_OUT = Symbol('push-timed-out');
/** Marks the permission prompt resolving, so the wall clock can start there. */
const PERMISSION_SETTLED = Symbol('push-permission-settled');

// ---------------------------------------------------------------------------
// Module-level VAPID key cache — only one fetch per browser session
// ---------------------------------------------------------------------------
let _cachedVapidPublicKey = null;

// ---------------------------------------------------------------------------
// In-flight revocation barrier
// ---------------------------------------------------------------------------

/**
 * A never-rejecting promise for the unsubscribe() currently in flight, or null.
 *
 * Why: logout fires unsubscribe() WITHOUT awaiting it (owner rule - a
 * best-effort cleanup must never block the primary flow), and logging out then
 * straight back in within the same document is a normal flow. unsubscribe()
 * revokes at the browser level BEFORE its DELETE lands, so without a barrier
 * this interleaving is reachable:
 *
 *   logout:  revoke browser subscription ------> DELETE /push/subscription
 *   login:                    create + POST ^                            ^
 *                                                        row deleted here
 *
 * Browsers may re-issue the SAME endpoint, and the DELETE is keyed on endpoint,
 * so the logout's DELETE removes the row the login just created. Net effect:
 * push silently dead for the rest of the document's life, with no marker and no
 * error - it only heals on the next full page load.
 *
 * silentResubscribe() therefore waits on this barrier before reading the
 * subscription state, which serialises "revoke fully, then restore". Logout
 * itself stays non-blocking: the barrier lives here, not in AuthContext.
 */
let _revocationInFlight = null;

// ---------------------------------------------------------------------------
// In-flight subscribe barrier
// ---------------------------------------------------------------------------

/**
 * The enable attempt currently running, or null: `{ bounded, settled }`.
 *
 * Two jobs, and they are held apart on purpose:
 *
 *  1. RE-ENTRY. A double-clicked CTA, or Settings and the dashboard nudge
 *     racing in one document, must not call `pushManager.subscribe()` twice -
 *     each call mints a real endpoint against the device's push-service quota.
 *     A second `startSubscribe()` JOINS this record instead.
 *
 *  2. ORDERING. `silentResubscribe()` and the revocation path wait on it so a
 *     background sync cannot interleave with a half-finished enable.
 *
 * ⚠ THE DEADLOCK RULE. The re-entry guard is released when `bounded` resolves -
 * that is, on WALL CLOCK, not on the attempt actually finishing. A guard tied to
 * `settled` would be permanent on the exact failure this whole change exists to
 * fix: a `pushManager.subscribe()` that never settles would kill the button for
 * the life of the document, which is strictly worse than the hang it replaced.
 * The cost of the time-based release is that a second press can start a second
 * `pushManager.subscribe()`; browsers coalesce concurrent calls carrying the
 * same `applicationServerKey`, so that is accepted.
 *
 * Everything that waits on this barrier applies its OWN bound
 * (SUBSCRIBE_BARRIER_WAIT_MS) - `bounded` itself only starts its clock once the
 * permission prompt resolves, and a prompt can stay open indefinitely.
 *
 * ⚠ CAVEAT, and it is the price of the rule above: releasing on wall clock also
 * ends the ORDERING job. Past 15s an attempt is still running with no barrier
 * around it, so a logout revocation or a silent restore CAN now interleave with
 * its registration POST. That is deliberate - the alternative is a barrier that
 * never releases - and it is bounded in blast radius: the registration POST is
 * an idempotent upsert on the backend, and `silentResubscribe` re-POSTs the
 * same endpoint, so the losing order costs a redundant round trip rather than a
 * lost subscription. The one genuinely destructive interleaving, a logout
 * DELETE landing on a row a login just created, is still covered by
 * `_revocationInFlight`, which has no wall clock.
 */
let _subscribeInFlight = null;

/**
 * How many enable attempts are inside their registration POST right now.
 *
 * The reconciliation poller and the attempt it is watching would otherwise
 * register the SAME endpoint concurrently every few seconds. The backend upsert
 * is atomic so that is survivable, but it is still two writes racing for no
 * reason, and the reconciliation's answer would be a coin toss between the two
 * responses. While the attempt is mid-POST the honest verdict is `unknown`:
 * nothing has been found out yet.
 *
 * A counter, not a boolean - two joined surfaces are one attempt, but nothing
 * stops a second attempt starting after the wall clock released the guard.
 */
let _registrationPostsInFlight = 0;

/**
 * Whether the current enable attempt still owes a `push_subscribed` event.
 *
 * Set when an attempt starts, cleared by whichever path first observes the
 * device registered - the attempt's own success, or a reconciliation that
 * noticed it came good. Without it, an attempt whose subscription is spotted by
 * reconcile AND then also settles successfully would count one enablement
 * twice.
 */
let _subscribedEventOwed = false;

/**
 * Emit `push_subscribed` at most once per attempt.
 *
 * @param {string} surface the surface to attribute it to
 * @returns {void}
 */
function _trackSubscribedOnce(surface) {
  if (!_subscribedEventOwed) return;
  _subscribedEventOwed = false;
  track('push_subscribed', { surface });
}

// ---------------------------------------------------------------------------
// Deliberate opt-out marker
// ---------------------------------------------------------------------------

/**
 * localStorage key recording that the user DELIBERATELY turned push off on this
 * device (the "Disable" / "כבה" button in Settings).
 *
 * Why it exists: logout revokes this device's subscription, because
 * `push_subscriptions` is unique on (user_id, endpoint) and without revoking it
 * one browser could hold live subscriptions for two accounts - user A's
 * opportunity titles landing on user B's lock screen. That revocation leaves
 * "permission is granted but there is no browser subscription" ambiguous:
 *   (a) we revoked it at logout  -> push must come back on the next login
 *   (b) the user pressed Disable -> push must stay off
 * Only (b) writes this key, which is what lets silentResubscribe() tell the two
 * apart instead of guessing. Logout must NEVER write it.
 *
 * Why localStorage rather than the backend: `push_preferences.push_enabled` is a
 * per-USER flag shared by every device on the account, while this is a
 * per-DEVICE choice - someone who disables push on their laptop must keep it on
 * their phone. localStorage is per-origin, survives logout and browser restarts,
 * and matches the device-scoped preference precedent already in the app
 * (PushNotificationsAnnounce, LabelsAnnounce, PublicGroupsNudge).
 * sessionStorage would be wrong: it dies with the tab, so a Disable would
 * silently undo itself the next morning.
 *
 * Deliberately NOT scoped per user id: it is a property of the browser, not of
 * an account, and an unscoped key errs toward "stay off" if a second account
 * later uses the same device - the safe direction. Enabling push in Settings
 * clears it, so the state is never stuck.
 */
const PUSH_OPT_OUT_KEY = 'leadify_push_opt_out';

/**
 * Whether the user deliberately turned push off on this device.
 *
 * FAILS CLOSED: localStorage throws (not returns null) in Safari private mode
 * and under some enterprise policies. An unreadable marker is treated as
 * PRESENT, so an unknown state never auto-resubscribes someone who may have
 * opted out. The cost is that a user with storage blocked has to re-enable push
 * from Settings after a logout - the wrong-but-safe side of the trade.
 *
 * @returns {boolean} true if push must stay off on this device
 */
export function hasDeliberateOptOut() {
  try {
    return localStorage.getItem(PUSH_OPT_OUT_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Record that the user deliberately turned push off on this device.
 *
 * Call ONLY from the Settings "Disable" action - never from logout.
 *
 * @returns {void}
 */
export function markDeliberateOptOut() {
  try {
    localStorage.setItem(PUSH_OPT_OUT_KEY, '1');
  } catch {
    /* Storage blocked. hasDeliberateOptOut() already fails closed, so the
       opt-out is still honoured - it just cannot be distinguished from any
       other unreadable-storage case. */
  }
}

/**
 * Clear the opt-out marker, re-arming automatic restoration on this device.
 *
 * Call ONLY from the Settings "Enable" action.
 *
 * @returns {void}
 */
export function clearDeliberateOptOut() {
  try {
    localStorage.removeItem(PUSH_OPT_OUT_KEY);
  } catch {
    /* Storage blocked - nothing was ever written, so nothing to clear. */
  }
}

// ---------------------------------------------------------------------------
// Private helpers — timing
// ---------------------------------------------------------------------------

/**
 * A cancellable timer promise resolving to TIMED_OUT.
 *
 * `cancel()` is not hygiene - an uncancelled `setTimeout` keeps the event loop
 * (and a jest fake-timer queue) alive long after the race it belonged to.
 *
 * @param {number} ms
 * @returns {{ promise: Promise<symbol>, cancel: () => void }}
 */
function _timeoutHandle(ms) {
  let timer = null;
  const promise = new Promise((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), ms);
  });
  return {
    promise,
    cancel: () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}

/**
 * ABORTIVE bound: resolves TIMED_OUT if `promise` has not settled in `ms`.
 *
 * Use ONLY where abandoning the await cannot strand state - the service-worker
 * wait and the read-only reconciliation. Never around `pushManager.subscribe()`
 * or the registration POST: abandoning either is how a browser subscription
 * ends up alive with no backend row, which is the bug this file works around
 * everywhere else.
 *
 * Rejections propagate; callers that can see one wrap this in try/catch.
 *
 * @param {Promise<*>} promise
 * @param {number} ms
 * @param {string} step - name used in the stall log
 * @returns {Promise<*|symbol>}
 */
function _withTimeout(promise, ms, step) {
  const handle = _timeoutHandle(ms);
  return Promise.race([Promise.resolve(promise), handle.promise])
    .then((value) => {
      if (value === TIMED_OUT) console.warn(`[Push] stalled at ${step} (>${ms}ms) - giving up on this step`);
      return value;
    })
    .finally(() => handle.cancel());
}

/**
 * DIAGNOSTIC bound: logs which step is slow and keeps waiting.
 *
 * This is the whole reason per-step bounds exist at all. The old code could
 * only report "subscribe() failed" with no way to tell a dead service worker
 * from a stalled push service from a hanging POST; a support report of "the
 * button does nothing" was unactionable. The await is deliberately NOT
 * abandoned - see `_withTimeout`.
 *
 * @param {Promise<*>} promise
 * @param {number} ms
 * @param {string} step
 * @returns {Promise<*>} the original promise's result or rejection
 */
function _logStall(promise, ms, step) {
  const timer = setTimeout(() => {
    console.warn(`[Push] stalled at ${step} (>${ms}ms) - still waiting`);
  }, ms);
  const clear = () => clearTimeout(timer);
  return Promise.resolve(promise).then(
    (value) => {
      clear();
      return value;
    },
    (err) => {
      clear();
      throw err;
    },
  );
}

/** A promise plus its resolver, for signalling across a flow boundary. */
function _deferred() {
  let resolve;
  const promise = new Promise((r) => {
    resolve = r;
  });
  return { promise, resolve };
}

/**
 * Wait for any in-flight enable attempt, with our OWN bound.
 *
 * @returns {Promise<void>} always resolves; never rejects
 */
async function _awaitSubscribeBarrier() {
  const inFlight = _subscribeInFlight;
  if (!inFlight) return;
  const handle = _timeoutHandle(SUBSCRIBE_BARRIER_WAIT_MS);
  try {
    await Promise.race([inFlight.bounded, handle.promise]);
  } catch {
    /* `bounded` is built from a never-rejecting chain; belt and braces. */
  } finally {
    handle.cancel();
  }
}

/**
 * Whether a failed registration POST is a TERMINAL refusal or merely
 * inconclusive.
 *
 * A 4xx is the backend saying no (e.g. 422 on a push-service host outside its
 * allowlist) - that answer will not change on a retry, so the device is simply
 * not subscribed. A network error, a timeout or a 5xx says nothing about the
 * subscription at all, and reporting those as "not subscribed" is the same
 * false-negative this module exists to avoid.
 *
 * @param {*} err
 * @returns {boolean}
 */
function _isTerminalRejection(err) {
  const status = err?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Convert a base64url-encoded VAPID public key string to a Uint8Array
 * as required by PushManager.subscribe({ applicationServerKey }).
 *
 * Steps:
 *  1. Re-pad the base64url string to a multiple of 4 characters.
 *  2. Replace URL-safe characters (- → +, _ → /) to get standard base64.
 *  3. Decode with atob() to a binary string.
 *  4. Copy each character code into a Uint8Array.
 *
 * This is the canonical implementation — do NOT simplify.
 *
 * @param {string} base64String - base64url-encoded VAPID public key
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Convert an ArrayBuffer (returned by PushSubscription.getKey()) to a
 * base64-encoded string for JSON serialisation to the backend.
 *
 * @param {ArrayBuffer} buffer
 * @returns {string} base64 string
 */
function arrayBufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

/**
 * Create a browser-level push subscription against the backend's VAPID key.
 *
 * Does NOT touch Notification.permission. Callers are responsible for the
 * permission state: subscribe() asks for it first, silentResubscribe() only
 * calls this when permission is ALREADY "granted", so pushManager.subscribe()
 * resolves without showing a prompt.
 *
 * @param {ServiceWorkerRegistration} registration - an active SW registration
 * @returns {Promise<PushSubscription|null>} the subscription, or null on failure
 */
async function createBrowserSubscription(registration) {
  // Both steps carry a DIAGNOSTIC bound (log only, the await continues) so a
  // stall names itself. They are applied here, not at the call sites, so the
  // silent restore path gets the same diagnostics as the user-facing one.
  const vapidPublicKey = await _logStall(getVapidPublicKey(), VAPID_KEY_STALL_MS, 'vapid_key');
  if (!vapidPublicKey) {
    console.error('[Push] Could not retrieve VAPID public key');
    return null;
  }

  return _logStall(
    registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }),
    PUSH_SUBSCRIBE_STALL_MS,
    'push_subscribe',
  );
}

/**
 * Upsert a browser subscription onto the backend.
 *
 * Shared by subscribe() and silentResubscribe() so the payload shape can only
 * be defined once - the same reasoning as toPushPrefsState() in
 * EmailPreferences.js, where two hand-kept copies silently diverged.
 *
 * @param {PushSubscription} pushSubscription
 * @returns {Promise<boolean>} true if the backend accepted the subscription
 */
async function registerSubscriptionWithBackend(pushSubscription) {
  const p256dhBuffer = pushSubscription.getKey('p256dh');
  const authBuffer = pushSubscription.getKey('auth');

  if (!p256dhBuffer || !authBuffer) {
    console.error('[Push] Could not extract p256dh or auth keys from subscription');
    return false;
  }

  await _logStall(
    api.post('/users/push/subscription', {
      endpoint: pushSubscription.endpoint,
      p256dh: arrayBufferToBase64(p256dhBuffer),
      auth: arrayBufferToBase64(authBuffer),
      device_name: navigator.userAgent.slice(0, 255),
    }),
    BACKEND_POST_STALL_MS,
    'backend_post',
  );

  return true;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check whether Web Push is supported in the current browser.
 *
 * Returns true only when BOTH the Service Worker API and the PushManager API
 * are available. Falls back to false on Safari iOS and older browsers that
 * lack one or both APIs.
 *
 * @returns {boolean}
 */
export function isSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Return the current Notification permission status.
 *
 * @returns {'default' | 'granted' | 'denied'}
 */
export function getPermissionStatus() {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission and return the RAW browser verdict.
 *
 * NEVER time-bounded. The prompt is a modal the user may sit on for a minute -
 * a timeout here would report a failure for someone who is simply reading it,
 * and the browser would then grant the permission behind our back.
 *
 * 'default' is a real answer, not an error: it means the prompt was dismissed
 * (the X, or Escape) rather than blocked, and unlike 'denied' it can be asked
 * again. Callers that collapse the two send a user to their browser settings
 * for a permission that was never actually blocked.
 *
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
async function _requestPermissionResult() {
  try {
    // No Notification API at all - indistinguishable from blocked, and the
    // one case that must NOT emit a permission-result event (nothing was asked).
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    // The resolution point, whichever way it went. Fired on an
    // already-granted permission too (the browser resolves without prompting),
    // because "resolved granted" is the fact this event reports.
    track('push_permission_result', { result });
    return result === 'granted' || result === 'denied' ? result : 'default';
  } catch (err) {
    console.error('[Push] requestPermission error:', err);
    return 'denied';
  }
}

/**
 * Request notification permission from the user.
 *
 * @returns {Promise<boolean>} true if permission was granted, false otherwise
 */
export async function requestPermission() {
  return (await _requestPermissionResult()) === 'granted';
}

/**
 * Fetch the VAPID public key from the backend.
 * Result is cached at module level — only one network call per page session.
 *
 * @returns {Promise<string|null>} VAPID public key string, or null on failure
 */
export async function getVapidPublicKey() {
  if (_cachedVapidPublicKey) return _cachedVapidPublicKey;

  try {
    const response = await api.get('/users/push/vapid-public-key');
    _cachedVapidPublicKey = response.data.public_key;
    return _cachedVapidPublicKey;
  } catch (err) {
    console.error('[Push] Failed to fetch VAPID public key:', err);
    return null;
  }
}

/**
 * Subscribe this device to Web Push notifications.
 *
 * The boolean face of `startSubscribe()`, kept for the fire-and-forget install
 * callers (`utils/pwaInstall.js`, `components/InstallPrompt.js`). Semantics are
 * UNCHANGED from before the time bounds existed: true only if the backend
 * accepted the subscription, false on any refusal or failure, never throws, and
 * it keeps waiting while the attempt is still running.
 *
 * Any surface that RENDERS state should call `startSubscribe()` instead - a
 * boolean cannot distinguish "failed" from "still working", and rendering the
 * second as the first is the whole of backlog #160.
 *
 * Full 9-step flow:
 *  1. Check browser support via isSupported()
 *  2. Request notification permission via requestPermission()
 *  3. Wait for the active service worker registration
 *  4. Fetch the VAPID public key (cached after first call)
 *  5. Convert the VAPID key from base64url to Uint8Array
 *  6. Subscribe via pushManager.subscribe()
 *  7. Extract endpoint, p256dh, auth from the subscription object
 *  8. POST the subscription data to the backend
 *  9. Return true on success
 *
 * @param {string} [surface] which UI asked - 'settings' | 'nudge' | 'install'.
 *   Instrumentation only; it changes nothing about the flow.
 * @returns {Promise<boolean>} true if subscription succeeded, false otherwise
 */
export async function subscribe(surface = DEFAULT_SURFACE) {
  const { outcome, settled } = await startSubscribe({ surface });
  // PENDING is not an answer, and this signature has no way to say so. The two
  // remaining boolean callers are fire-and-forget install paths that hold no UI
  // state to reconcile, so waiting for the real answer is exactly the behaviour
  // they had before the bounds existed.
  if (outcome === SUBSCRIBE_OUTCOME.PENDING) {
    return (await settled) === SUBSCRIBE_OUTCOME.SUCCESS;
  }
  return outcome === SUBSCRIBE_OUTCOME.SUCCESS;
}

/**
 * Subscribe this device, reporting a BOUNDED outcome plus a handle on the real
 * one.
 *
 * Same flow as `subscribe()` - support gate, permission, service worker, VAPID
 * key, `pushManager.subscribe()`, backend upsert - with one difference that is
 * the entire point: it stops WAITING after `timeoutMs` (measured from the
 * moment the permission prompt resolves) and says PENDING, while the attempt
 * carries on in the background.
 *
 * A caller must treat the two returned values differently:
 *   - `outcome` is what to render NOW. PENDING means "still working", and a UI
 *     that renders it as a failure re-creates the bug this replaced: the
 *     subscription frequently DOES come up a few seconds later.
 *   - `settled` never rejects and resolves with the real outcome if the attempt
 *     ever finishes. It may never resolve - a `pushManager.subscribe()` that
 *     hangs forever is the reported failure mode - so it must never gate
 *     anything the user is waiting on.
 *
 * Concurrent calls JOIN the running attempt rather than starting a second one
 * (see `_subscribeInFlight`).
 *
 * @param {object} [options]
 * @param {number} [options.timeoutMs] wall clock, started after the permission
 *   prompt resolves. Never applied to the prompt itself.
 * @param {string} [options.surface] which UI asked - instrumentation only.
 * @returns {Promise<{outcome: string, settled: Promise<string>}>} always
 *   resolves; never throws to the caller.
 */
export async function startSubscribe({
  timeoutMs = SUBSCRIBE_WALL_CLOCK_MS,
  surface = DEFAULT_SURFACE,
} = {}) {
  if (_subscribeInFlight) return _subscribeInFlight.bounded;

  // Arm the single `push_subscribed` this attempt is allowed to emit, whichever
  // path ends up observing the device registered.
  _subscribedEventOwed = true;

  const permissionResolved = _deferred();
  // Never rejects: `_runSubscribe` catches everything and reports an outcome.
  const settled = _runSubscribe(surface, permissionResolved);

  const record = { settled, bounded: null };

  record.bounded = (async () => {
    // Phase 1 — the permission prompt. UNBOUNDED, deliberately. The attempt can
    // also finish inside this phase (unsupported, or a throw), so both are raced.
    const first = await Promise.race([
      settled,
      permissionResolved.promise.then(() => PERMISSION_SETTLED),
    ]);
    if (first !== PERMISSION_SETTLED) return { outcome: first, settled };

    // Phase 2 — everything the user is not being asked about. The wall clock
    // starts HERE.
    const handle = _timeoutHandle(timeoutMs);
    try {
      const raced = await Promise.race([settled, handle.promise]);
      if (raced === TIMED_OUT) {
        console.warn(`[Push] subscribe still running after ${timeoutMs}ms - reporting PENDING`);
        return { outcome: SUBSCRIBE_OUTCOME.PENDING, settled };
      }
      return { outcome: raced, settled };
    } finally {
      handle.cancel();
    }
  })().then((result) => {
    // TIME-based release of the re-entry guard - see the _subscribeInFlight
    // warning. Tying it to `settled` would make a never-settling attempt
    // permanently disable every enable button in the document.
    if (_subscribeInFlight === record) _subscribeInFlight = null;
    return result;
  });

  _subscribeInFlight = record;
  return record.bounded;
}

/**
 * The enable attempt itself. Resolves a SUBSCRIBE_OUTCOME; never rejects.
 *
 * @param {string} surface instrumentation discriminator
 * @param {{resolve: Function}} permissionResolved signalled the moment the
 *   permission prompt settles, so the caller's wall clock can start there
 * @returns {Promise<string>}
 */
async function _runSubscribe(surface, permissionResolved) {
  try {
    // Step 1 — Browser support gate
    if (!isSupported()) {
      console.warn('[Push] Web Push not supported in this browser');
      return SUBSCRIBE_OUTCOME.UNSUPPORTED;
    }

    // Step 2 — Permission. No bound; the clock starts when this resolves.
    const permission = await _requestPermissionResult();
    permissionResolved.resolve();

    if (permission === 'denied') {
      console.warn('[Push] Notification permission denied');
      return SUBSCRIBE_OUTCOME.DENIED;
    }
    if (permission !== 'granted') {
      // Prompt closed without an answer. Retryable, unlike DENIED.
      console.warn('[Push] Notification permission prompt dismissed');
      return SUBSCRIBE_OUTCOME.DISMISSED;
    }

    // Step 3 — Wait for active SW registration. The one ABORTIVE bound: this
    // promise never rejects, so a registration that failed leaves it pending
    // for the life of the document. Nothing exists yet, so nothing is stranded.
    const registration = await _withTimeout(
      navigator.serviceWorker.ready,
      SW_READY_TIMEOUT_MS,
      'sw_ready',
    );
    if (registration === TIMED_OUT) return SUBSCRIBE_OUTCOME.FAILED;

    // Steps 4-6 — VAPID key (cached), base64url → Uint8Array, PushManager.
    // Diagnostically bounded inside createBrowserSubscription, never abandoned.
    const pushSubscription = await createBrowserSubscription(registration);
    if (!pushSubscription) return SUBSCRIBE_OUTCOME.FAILED;

    // This device now HOLDS a live browser subscription, so by definition it is
    // no longer opted out - withdraw any stale marker.
    //
    // It belongs here rather than at the call sites because every caller of
    // subscribe() is an explicit user action (Settings "Enable", or push
    // auto-enable after an accepted PWA install in the navbar, the install
    // banner, the announce dialog and this page). Leaving it to the callers is
    // what let a user who had pressed Disable install the app, get working
    // push, then lose it permanently at the next logout - silentResubscribe
    // would see no subscription plus a stale marker and stand down forever.
    //
    // Placed AFTER creation and not at the top of this function on purpose. A
    // subscribe() that fails at the permission prompt leaves push off, and
    // clearing the marker there would mean a later browser-level permission
    // grant silently auto-subscribed someone whose only successful deliberate
    // action was Disable. Tying the clear to an actually-created subscription
    // keeps the marker a statement about observable device state.
    clearDeliberateOptOut();

    // Steps 7-8 — Extract keys and POST to backend (upsert). Flagged for the
    // whole duration so a reconciliation poll running beside this attempt does
    // not fire a competing registration of the same endpoint.
    _registrationPostsInFlight += 1;
    let registered;
    try {
      registered = await registerSubscriptionWithBackend(pushSubscription);
    } finally {
      _registrationPostsInFlight -= 1;
    }
    if (!registered) return SUBSCRIBE_OUTCOME.FAILED;

    // Step 9 — Done. Fired only once the BACKEND accepted the subscription,
    // which is the only state in which this device can actually be reached.
    _trackSubscribedOnce(surface);
    console.info('[Push] Subscription registered successfully');
    return SUBSCRIBE_OUTCOME.SUCCESS;

  } catch (err) {
    console.error('[Push] subscribe() failed:', err);
    return SUBSCRIBE_OUTCOME.FAILED;
  }
}

/**
 * Silently restore this device's push subscription on an authenticated load.
 *
 * Called from AuthContext on every login / authenticated load. Requires no user
 * interaction and NEVER prompts for permission.
 *
 * Two cases, both handled here:
 *
 *  1. A browser subscription EXISTS — re-register it with the backend. This
 *     reactivates a row the backend marked inactive after a transient FCM/APNs
 *     error, so the user does not miss notifications until they happen to visit
 *     Settings.
 *
 *  2. A browser subscription is MISSING while permission is still "granted" —
 *     create a fresh one, but only when the deliberate opt-out marker is
 *     absent. This is the logout case: logout revokes the subscription so one
 *     browser cannot hold live subscriptions for two accounts, which without
 *     this branch left push silently dead on the device until the user
 *     re-enabled it by hand. `hasDeliberateOptOut()` is what keeps this from
 *     also overriding a user who pressed Disable in Settings, and it fails
 *     closed - an unreadable marker means we do nothing.
 *
 * Skips silently if push is unsupported, if Notification is undefined, or if
 * permission is anything other than "granted". That last check is load-bearing
 * twice over: it is the reason pushManager.subscribe() below cannot raise a
 * permission prompt from what must remain a silent background path.
 *
 * The return value reports BACKEND acceptance, not merely the presence of a
 * browser subscription — those are different states. A browser can hold a
 * perfectly live subscription that our API rejects (e.g. a push-service host
 * outside the backend's allowlist returns 422), and a UI that reads the browser
 * object alone would then claim push is on while no notification can ever be
 * delivered. Callers that render subscription state must use this value.
 *
 * @returns {Promise<boolean>} true only if this device is registered with the
 *   backend. Always resolves; never throws to the caller.
 */
export async function silentResubscribe() {
  try {
    if (!isSupported()) return false;
    if (typeof Notification === 'undefined') return false;
    // Never prompt from a silent path — act only on an already-granted
    // permission. 'default' and 'denied' both stand down.
    if (Notification.permission !== 'granted') return false;

    // Serialise behind a logout's in-flight revocation, so a fast log-out /
    // log-back-in cannot have that revocation's DELETE erase the row we are
    // about to create. See _revocationInFlight.
    if (_revocationInFlight) await _revocationInFlight;
    // ...and behind a user-initiated enable, so a background sync cannot
    // interleave with a half-finished attempt. Bounded on our side - the
    // attempt may be parked on a permission prompt.
    await _awaitSubscribeBarrier();

    // create:true is the logout-restore branch; unbounded because nothing is
    // waiting on this and abandoning a step would strand a browser
    // subscription with no backend row.
    const verdict = await _syncExistingSubscription({ create: true });
    return verdict === RECONCILE_RESULT.SUCCESS;
  } catch (err) {
    // Fire-and-forget — never surface errors to the caller. A throw here is a
    // rejected registration (e.g. 422) as much as a network blip, so the
    // failure must read as NOT subscribed.
    console.warn('[Push] silentResubscribe failed (non-critical):', err);
    return false;
  }
}

/**
 * Read this device's real subscription state and, if a browser subscription
 * exists, make sure the backend has it.
 *
 * The post-hoc half of the #160 fix: after `startSubscribe()` reports PENDING,
 * the attempt may quietly succeed while the user is on another tab, so a
 * surface that showed "still working" needs a way to find out WITHOUT asking
 * again. It is READ + idempotent re-POST only:
 *
 *   - NEVER prompts (no `requestPermission`, at any permission state).
 *   - NEVER creates a browser subscription. `create:false` is what separates
 *     this from `silentResubscribe()`; a reconciliation that could mint an
 *     endpoint would fire the permission prompt behind the user's back on a
 *     tab-focus event.
 *
 * Verdicts:
 *   no browser subscription        -> 'not_subscribed'
 *   subscription + POST accepted   -> 'success'
 *   subscription + 4xx             -> 'not_subscribed' (terminal: the backend
 *                                     refuses this endpoint, e.g. 422 on a push
 *                                     host outside its allowlist - the orphan
 *                                     case, and the same rule the dashboard
 *                                     card applies via `hasActivePush === false`)
 *   subscription + network/timeout -> 'unknown' (inconclusive; claiming failure
 *                                     here is the false-negative this module
 *                                     exists to avoid)
 *
 * @param {object} [options]
 * @param {number} [options.timeoutMs] bound for the registration POST; the
 *   service-worker and getSubscription reads carry their own shorter bounds.
 * @returns {Promise<'success'|'not_subscribed'|'unknown'>} always resolves.
 */
export async function reconcileSubscription({ timeoutMs = RECONCILE_TIMEOUT_MS } = {}) {
  try {
    if (!isSupported()) return RECONCILE_RESULT.NOT_SUBSCRIBED;

    // The attempt this poll is watching is mid-registration. Answering now
    // would mean a second POST of the same endpoint racing the first, and a
    // verdict decided by whichever response came back - so stand down. Nothing
    // has been found out, which is exactly what `unknown` means.
    if (_registrationPostsInFlight > 0) return RECONCILE_RESULT.UNKNOWN;

    const verdict = await _syncExistingSubscription({ create: false, timeoutMs });
    if (verdict === RECONCILE_RESULT.SUCCESS) {
      // A real enablement that no button will ever report: the subscription
      // came good in the background and this read is what noticed. Fires at
      // most once per attempt (see _trackSubscribedOnce), so an attempt that is
      // spotted here AND then settles successfully is still one enablement.
      _trackSubscribedOnce(RECONCILE_SURFACE);
    }
    return verdict;
  } catch (err) {
    // A throw is as likely to be a blip as a refusal, and this path's whole
    // job is to avoid guessing. Say so.
    console.warn('[Push] reconcileSubscription failed (inconclusive):', err);
    return RECONCILE_RESULT.UNKNOWN;
  }
}

/**
 * The shared body of `silentResubscribe()` and `reconcileSubscription()`.
 *
 * Extracted rather than copied because the two differ in exactly two axes -
 * whether a missing subscription may be CREATED, and whether the steps are
 * time-bounded - and every other rule (the opt-out marker check, "backend
 * acceptance is what counts", the 4xx-vs-blip distinction) has to stay
 * identical or the two paths will disagree about whether push is on.
 *
 * @param {object} options
 * @param {boolean} options.create create a subscription when the browser holds
 *   none (the logout-restore branch). Honours the deliberate opt-out marker.
 * @param {number|null} [options.timeoutMs] null = unbounded. A number bounds
 *   the registration POST; the two reads use their own shorter constants.
 * @returns {Promise<'success'|'not_subscribed'|'unknown'>}
 */
async function _syncExistingSubscription({ create, timeoutMs = null }) {
  const bounded = timeoutMs !== null;

  const registration = bounded
    ? await _withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS, 'sw_ready')
    : await _logStall(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS, 'sw_ready');
  if (registration === TIMED_OUT) return RECONCILE_RESULT.UNKNOWN;

  const read = registration.pushManager.getSubscription();
  let pushSubscription = bounded
    ? await _withTimeout(read, GET_SUBSCRIPTION_TIMEOUT_MS, 'get_subscription')
    : await read;
  if (pushSubscription === TIMED_OUT) return RECONCILE_RESULT.UNKNOWN;

  if (!pushSubscription) {
    // No browser subscription. For a READ that is a definitive answer; for the
    // restore path it is ambiguous - revoked by our own logout, or turned off
    // by the user? The marker is the only thing that distinguishes them, and
    // silence (unreadable storage) counts as opted out.
    if (!create) return RECONCILE_RESULT.NOT_SUBSCRIBED;
    if (hasDeliberateOptOut()) return RECONCILE_RESULT.NOT_SUBSCRIBED;

    pushSubscription = await createBrowserSubscription(registration);
    if (!pushSubscription) return RECONCILE_RESULT.NOT_SUBSCRIBED;
    console.info('[Push] Restored subscription revoked at logout');
  }

  try {
    const post = registerSubscriptionWithBackend(pushSubscription);
    // 'reconcile_post', not 'backend_post': registerSubscriptionWithBackend
    // already wraps the same call in a DIAGNOSTIC stall log named
    // 'backend_post'. Two identical names on one await made the log ambiguous -
    // and since this outer bound (5s) is shorter than that inner one (8s), the
    // inner log could never fire on this path at all.
    const registered = bounded
      ? await _withTimeout(post, timeoutMs, 'reconcile_post')
      : await post;
    if (registered === TIMED_OUT) return RECONCILE_RESULT.UNKNOWN;
    return registered ? RECONCILE_RESULT.SUCCESS : RECONCILE_RESULT.NOT_SUBSCRIBED;
  } catch (err) {
    if (_isTerminalRejection(err)) {
      // Deliberately logged on BOTH paths. The 422-orphan - a browser holding a
      // live subscription our API refuses forever - is the failure that used to
      // present as "push is on and silent", and on the silent path it is
      // otherwise completely invisible: silentResubscribe swallows the throw
      // and simply reports false. Keep the console breadcrumb.
      console.warn(
        '[Push] registration REFUSED by the backend (orphaned browser subscription):',
        err?.response?.status,
      );
      return RECONCILE_RESULT.NOT_SUBSCRIBED;
    }
    // Inconclusive. silentResubscribe collapses this to false; a reconciling
    // UI must keep waiting rather than announce a failure.
    if (!bounded) throw err;
    console.warn('[Push] registration POST inconclusive:', err);
    return RECONCILE_RESULT.UNKNOWN;
  }
}

/**
 * Unsubscribe this device from Web Push notifications.
 *
 * Steps:
 *  1. Get current push subscription from PushManager
 *  2. If none exists — already unsubscribed, return true
 *  3. Call subscription.unsubscribe() to revoke at the browser level
 *  4. DELETE subscription from the backend
 *
 * @param {string} [surface] which UI asked - 'settings' for the Settings
 *   Disable button, 'logout' for the sign-out cleanup (which emits NO
 *   push_unsubscribed event - see SILENT_UNSUBSCRIBE_SURFACE).
 * @returns {Promise<boolean>} true if successfully unsubscribed (or was never subscribed)
 */
export async function unsubscribe(surface = DEFAULT_SURFACE) {
  const attempt = _revokeSubscription(surface);

  // Publish a never-rejecting handle for silentResubscribe() to wait on, and
  // retract it once this attempt settles - guarding against clobbering a newer
  // revocation that started in the meantime.
  const barrier = attempt.then(
    () => {},
    () => {},
  );
  _revocationInFlight = barrier;
  barrier.then(() => {
    if (_revocationInFlight === barrier) _revocationInFlight = null;
  });

  return attempt;
}

/**
 * The actual revocation, split out so unsubscribe() can publish its barrier
 * synchronously - before the first await - and callers still receive the
 * original promise and its boolean.
 *
 * @param {string} surface which UI asked - instrumentation only
 * @returns {Promise<boolean>}
 */
async function _revokeSubscription(surface) {
  try {
    if (!isSupported()) return true;

    // Symmetric with silentResubscribe: do not revoke out from under an enable
    // attempt that is still creating a subscription. Bounded on our side, so a
    // subscribe parked on a permission prompt cannot hold up a sign-out.
    await _awaitSubscribeBarrier();

    const registration = await navigator.serviceWorker.ready;
    const pushSubscription = await registration.pushManager.getSubscription();

    if (!pushSubscription) {
      // Nothing to unsubscribe
      return true;
    }

    const endpoint = pushSubscription.endpoint;

    // Revoke at browser level
    await pushSubscription.unsubscribe();

    // Notify backend to soft-delete the row
    await api.delete('/users/push/subscription', {
      data: { endpoint },
    });

    // After the backend accepted the DELETE - the mirror of push_subscribed,
    // so the two events count the same thing (a device the backend can reach
    // gained or lost), not merely a button press.
    //
    // Except at logout, which is hygiene rather than a decision - see
    // SILENT_UNSUBSCRIBE_SURFACE.
    if (surface !== SILENT_UNSUBSCRIBE_SURFACE) {
      track('push_unsubscribed', { surface });
    }

    console.info('[Push] Unsubscribed successfully');
    return true;

  } catch (err) {
    console.error('[Push] unsubscribe() failed:', err);
    return false;
  }
}

/**
 * Return the current PushSubscription for this device, or null if none.
 *
 * Used to determine whether this specific device is already subscribed
 * (e.g. to show correct UI state on settings page).
 *
 * @returns {Promise<PushSubscription|null>}
 */
export async function getCurrentSubscription() {
  try {
    if (!isSupported()) return null;

    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (err) {
    console.error('[Push] getCurrentSubscription() failed:', err);
    return null;
  }
}

/**
 * Fetch push preferences for the current user from the backend.
 *
 * @returns {Promise<Object|null>} preferences object, or null on failure
 */
export async function getPushPreferences() {
  try {
    const response = await api.get('/users/push/preferences');
    return response.data;
  } catch (err) {
    console.error('[Push] getPushPreferences() failed:', err);
    return null;
  }
}

/**
 * Update push preferences for the current user.
 *
 * @param {Object} prefs - Partial preferences object (any subset of fields)
 * @returns {Promise<Object|null>} updated preferences, or null on failure
 */
export async function updatePushPreferences(prefs) {
  try {
    const response = await api.put('/users/push/preferences', prefs);
    return response.data;
  } catch (err) {
    console.error('[Push] updatePushPreferences() failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Default export — convenience object for component imports
// ---------------------------------------------------------------------------
const pushSubscriptionService = {
  isSupported,
  getPermissionStatus,
  requestPermission,
  hasDeliberateOptOut,
  markDeliberateOptOut,
  clearDeliberateOptOut,
  getVapidPublicKey,
  subscribe,
  startSubscribe,
  reconcileSubscription,
  SUBSCRIBE_OUTCOME,
  silentResubscribe,
  unsubscribe,
  getCurrentSubscription,
  getPushPreferences,
  updatePushPreferences,
};

export default pushSubscriptionService;
